import { Dexie } from 'dexie';
import axios from 'axios';

class TalismanTokenRatesDatabase extends Dexie {
  constructor() {
    super("TalismanTokenRates");

    // https://dexie.org/docs/Tutorial/Design#database-versioning
    this.version(1).stores({
      // You only need to specify properties that you wish to index.
      // The object store will allow any properties on your stored objects but you can only query them by indexed properties
      // https://dexie.org/docs/API-Reference#declare-database
      //
      // Never index properties containing images, movies or large (huge) strings. Store them in IndexedDB, yes! but just don’t index them!
      // https://dexie.org/docs/Version/Version.stores()#warning
      tokenRates: "tokenId"
    });
  }
}
const db = new TalismanTokenRatesDatabase();

const SUPPORTED_CURRENCIES = {
  btc: {
    name: "Bitcoin",
    symbol: "₿"
  },
  eth: {
    name: "Ethereum",
    symbol: "Ξ"
  },
  dot: {
    name: "Polkadot",
    symbol: "D"
  },
  usd: {
    name: "US Dollar",
    symbol: "$"
  },
  cny: {
    name: "Chinese Yuan",
    symbol: "¥"
  },
  eur: {
    name: "Euro",
    symbol: "€"
  },
  gbp: {
    name: "British Pound",
    symbol: "£"
  },
  cad: {
    name: "Canadian Dollar",
    symbol: "C$"
  },
  aud: {
    name: "Australian Dollar",
    symbol: "A$"
  },
  nzd: {
    name: "New Zealand Dollar",
    symbol: "NZ$"
  },
  jpy: {
    name: "Japanese Yen",
    symbol: "¥"
  },
  rub: {
    name: "Russian Ruble",
    symbol: "₽"
  },
  krw: {
    name: "South Korean Won",
    symbol: "₩"
  },
  idr: {
    name: "Indonesian Rupiah",
    symbol: "Rp"
  },
  php: {
    name: "Philippine Peso",
    symbol: "₱"
  },
  thb: {
    name: "Thai Baht",
    symbol: "฿"
  },
  vnd: {
    name: "Vietnamese Dong",
    symbol: "₫"
  },
  inr: {
    name: "Indian Rupee",
    symbol: "₹"
  },
  try: {
    name: "Turkish Lira",
    symbol: "₺"
  },
  // hkd: { name: "Hong Kong Dollar", symbol: "HK$" },
  sgd: {
    name: "Singapore Dollar",
    symbol: "S$"
  }
  // twd: { name: "Taiwanese Dollar", symbol: "NT$" },
};

const NewTokenRates = () => ({
  btc: null,
  eth: null,
  dot: null,
  usd: null,
  cny: null,
  eur: null,
  gbp: null,
  cad: null,
  aud: null,
  nzd: null,
  jpy: null,
  rub: null,
  krw: null,
  idr: null,
  php: null,
  thb: null,
  vnd: null,
  inr: null,
  try: null,
  // hkd: null,
  sgd: null
  // twd: null,
});

// every currency in this list will be fetched from coingecko
// comment out unused currencies to save some bandwidth!
const coingeckoCurrencies = Object.keys(SUPPORTED_CURRENCIES);
// api returns a 414 error if the url is too long, max length is about 8100 characters
// so use 8000 to be safe
const MAX_COINGECKO_URL_LENGTH = 8000;
const DEFAULT_COINGECKO_CONFIG = {
  apiUrl: "https://api.coingecko.com"
};

// export function tokenRates(tokens: WithCoingeckoId[]): TokenRatesList {}
async function fetchTokenRates(tokens, config = DEFAULT_COINGECKO_CONFIG) {
  // create a map from `coingeckoId` -> `tokenId` for each token
  const coingeckoIdToTokenIds = Object.values(tokens)
  // ignore testnet tokens
  .filter(({
    isTestnet
  }) => !isTestnet).flatMap(token => {
    // BEGIN: LP tokens have a rate which is calculated later on, using the rates of two other tokens.
    //
    // This section contains the logic such that: if token is an LP token, then fetch the rates for the two underlying tokens.
    if (token.type === "evm-uniswapv2") {
      if (!token.evmNetwork) return [];
      const getToken = (evmNetworkId, tokenAddress, coingeckoId) => ({
        id: evmErc20TokenId(evmNetworkId, tokenAddress),
        coingeckoId
      });
      const token0 = token.coingeckoId0 ? [getToken(token.evmNetwork.id, token.tokenAddress0, token.coingeckoId0)] : [];
      const token1 = token.coingeckoId1 ? [getToken(token.evmNetwork.id, token.tokenAddress1, token.coingeckoId1)] : [];
      return [...token0, ...token1];
    }
    // END: LP tokens have a rate which is calculated later on, using the rates of two other tokens.

    // ignore tokens which don't have a coingeckoId
    if (!token.coingeckoId) return [];
    return [{
      id: token.id,
      coingeckoId: token.coingeckoId
    }];
  })

  // get each token's coingeckoId
  .reduce((coingeckoIdToTokenIds, {
    id,
    coingeckoId
  }) => {
    if (!coingeckoIdToTokenIds[coingeckoId]) coingeckoIdToTokenIds[coingeckoId] = [];
    coingeckoIdToTokenIds[coingeckoId].push(id);
    return coingeckoIdToTokenIds;
  }, {});

  // create a list of coingeckoIds we want to fetch
  const coingeckoIds = Object.keys(coingeckoIdToTokenIds).sort();

  // skip network request if there is nothing for us to fetch
  if (coingeckoIds.length < 1) return {};

  // construct a coingecko request, sort args to help proxies with caching

  const currenciesSerialized = coingeckoCurrencies.sort().join(",");
  // note: coingecko api key cannot be passed as header here as it would be camel cased by axios and ignored by the server
  // need to pass it as a query parameter, and replace all '-' with '_'
  // TODO => migrate to fetch api
  const apiKeySuffix = config.apiKeyName && config.apiKeyValue ? `&${config.apiKeyName?.replaceAll("-", "_")}=${config.apiKeyValue}` : "";
  const safelyGetCoingeckoUrls = coingeckoIds => {
    const idsSerialized = coingeckoIds.join(",");
    const queryUrl = `${config.apiUrl}/api/v3/simple/price?ids=${idsSerialized}&vs_currencies=${currenciesSerialized}${apiKeySuffix}`;
    if (queryUrl.length > MAX_COINGECKO_URL_LENGTH) {
      const half = Math.floor(coingeckoIds.length / 2);
      return [...safelyGetCoingeckoUrls(coingeckoIds.slice(0, half)), ...safelyGetCoingeckoUrls(coingeckoIds.slice(half))];
    }
    return [queryUrl];
  };

  // fetch the token prices from coingecko
  // the response should be in the format:
  // {
  //   [coingeckoId]: {
  //     [currency]: rate
  //   }
  // }
  const coingeckoPrices = await Promise.all(safelyGetCoingeckoUrls(coingeckoIds).map(async queryUrl => await axios.get(queryUrl).then(response => response.data))).then(responses => Object.assign({}, ...responses));

  // build a TokenRatesList from the token prices result
  const ratesList = Object.fromEntries(coingeckoIds.flatMap(coingeckoId => {
    const tokenIds = coingeckoIdToTokenIds[coingeckoId];
    const rates = NewTokenRates();
    for (const currency of coingeckoCurrencies) {
      rates[currency] = ((coingeckoPrices || {})[coingeckoId] || {})[currency] || null;
    }
    return tokenIds.map(tokenId => [tokenId, rates]);
  }));

  // return the TokenRatesList
  return ratesList;
}

// TODO: Move this into a common module which can then be imported both here and into EvmErc20Module
// We can't import this directly from EvmErc20Module because this package doesn't depend on `@talismn/balances`
const evmErc20TokenId = (chainId, tokenContractAddress) => `${chainId}-evm-erc20-${tokenContractAddress}`.toLowerCase();

export { DEFAULT_COINGECKO_CONFIG, NewTokenRates, SUPPORTED_CURRENCIES, TalismanTokenRatesDatabase, db, fetchTokenRates };
